import { useEffect, useState } from 'react'

import { useExternalAccountsQuery } from '@/graphql/generated/hooks'
import { StreamingType } from '@/graphql/generated/schemas'
import {
  OptionI,
  getDefaultExternalAccountOption,
  getExternalAccountOptions,
} from '@/utils/forms/optionBuilders'

import {
  getDefaultOrganizationId,
  getExternalAccount,
  getOrganizationId,
  shouldHandleExternalAccountField,
} from '../utils/utils'

type SetValueFnType = (
  name: string,
  value: unknown,
  config?: Partial<{
    shouldValidate: boolean
    shouldDirty: boolean
  }>
) => void

interface UseEditExternalAccountIProps {
  streamingType: OptionI<StreamingType>
  externalAccountId: OptionI
  setValue: SetValueFnType
  initialExternalAccountId: string
}

export const useEditExternalAccount = ({
  streamingType,
  externalAccountId,
  setValue,
  initialExternalAccountId,
}: UseEditExternalAccountIProps) => {
  const [organizationId, setOrganizationId] = useState(null)

  const shouldShowOrganizationId =
    Boolean(externalAccountId?.value) || Boolean(organizationId)
  const shouldShowExternalAccount = shouldHandleExternalAccountField(
    streamingType?.value
  )
  const { data, loading: isExternalAccountLoading } = useExternalAccountsQuery({
    fetchPolicy: 'network-only',
    skip: !shouldShowExternalAccount,
  })

  // Only accounts whose ExternalSystemType matches the streaming type are
  // offered: the backend rejects any other pairing on save, and the
  // default account it falls back to is per system type too.
  const defaultExternalAccount = getDefaultExternalAccountOption(
    data,
    streamingType?.value
  )

  const externalAccountOptions = getExternalAccountOptions(
    data,
    streamingType?.value
  )

  useEffect(() => {
    if (data && !isExternalAccountLoading) {
      // Re-resolving against the filtered options also clears an account that
      // no longer matches after the streaming type changed.
      setValue(
        'externalAccountId',
        getExternalAccount(externalAccountOptions, initialExternalAccountId)
      )
      setOrganizationId(
        getOrganizationId(data, externalAccountId) ??
          getDefaultOrganizationId(data, streamingType?.value)
      )
    }
  }, [isExternalAccountLoading, streamingType?.value])

  useEffect(() => {
    setOrganizationId(
      getOrganizationId(data, externalAccountId) ??
        getDefaultOrganizationId(data, streamingType?.value)
    )
  }, [externalAccountId?.value])

  return {
    externalAccountOptions,
    shouldShowExternalAccount,
    shouldShowOrganizationId,
    organizationId,
    defaultExternalAccount,
  }
}
